/* eslint-disable react-hooks/exhaustive-deps */
import InfoIcon from '@mui/icons-material/Info';
import { Formik } from 'formik';
import { useCallback, useEffect, useState } from 'react';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import Select from 'react-select';
import * as yup from 'yup';
import { customerHandler, masterHandler } from '../../../redux/handlers';
import BusinessImageUpload from './BusinessImageUpload';

const validationSchema = yup.object().shape({
  settingTaxNumber: yup.string().required('กรุณากรอกเลขประจำตัวผู้เสียภาษี'),
  settingSlug: yup.string().required('กรุณากรอก URL ธุรกิจ'),
  settingEntityTypeId: yup.string().required('กรุณาเลือกประเภทกิจการ'),
  settingPrefixNameId: yup.string().required('กรุณาเลือกคำนำหน้า'),
  settingFirstName: yup.string().when('settingEntityTypeId', {
    is: '2',
    then: () => yup.string().required('กรุณากรอกชื่อจริง'),
    otherwise: () => yup.string().nullable()
  }),
  settingCompanyName: yup.string().when('settingEntityTypeId', {
    is: '1',
    then: () => yup.string().required('กรุณากรอกชื่อบริษัท'),
    otherwise: () => yup.string().nullable()
  }),
  settingLastName: yup.string().when('settingEntityTypeId', {
    is: '2',
    then: () => yup.string().required('กรุณากรอกนามสกุล'),
    otherwise: () => yup.string().nullable()
  }),
  settingRestaurantTypeId: yup.string().required('กรุณาเลือกประเภทร้านอาหาร'),
  settingShopProductCategoryTypeId: yup.string().required('กรุณาเลือกประเภทสินค้า')
});

const defaultInitialValues = {
  imagePath: '',
  settingSlug: '',
  settingTaxNumber: '',
  settingEntityTypeId: '',
  settingPrefixNameId: '',
  settingCompanyName: '',
  settingFirstName: '',
  settingLastName: '',
  settingCompanyLastName: '',
  settingAddress: '',
  settingSubDistrictId: '',
  settingDistrict: '',
  settingProvince: '',
  settingPostalCode: '',
  settingRestaurantTypeId: '',
  settingShopProductCategoryTypeId: '',
  businessGroupId: ''
};

const BusinessSettingsTabInfo = ({ onSuccess }) => {
  const dispatch = useDispatch();
  const { businessID } = useParams();
  const [formDisabled, setFormDisabled] = useState(true);

  const selectedBusiness = useSelector(state => state.customer.businesses?.business);
  const selectedCustomer = useSelector(state => state.customer.customer);

  const isCreateMode = !selectedBusiness;

  const { entityTypes, prefixNames, shopProductCategories, restaurantTypes, provinces, districts, subDistricts } =
    useSelector(state => state.master);

  const [initialValues, setInitialValues] = useState(defaultInitialValues);

  useEffect(() => {
    dispatch(masterHandler.getPrefixNames(selectedBusiness?.entity_type_id === 1 ? 'JURISTIC' : 'NATURAL'));
    if (isCreateMode) {
      setFormDisabled(false);
    }
  }, [isCreateMode, selectedBusiness]);

  const createOptions = useCallback((data = [], labelKey = 'name', valueKey = 'id') => {
    return data?.map(item => ({
      value: item[valueKey].toString(),
      label: item[labelKey],
      ...item
    }));
  }, []);

  const businessTypeOptions = createOptions(entityTypes);
  const prefixTypeOptions = createOptions(prefixNames);
  const productTypeOptions = createOptions(shopProductCategories);
  const restaurantTypeOptions = createOptions(restaurantTypes);

  useEffect(() => {
    if (isCreateMode) {
      setInitialValues(defaultInitialValues);
      setFormDisabled(false);
    } else if (selectedBusiness) {
      const {
        image_path,
        tax_number,
        company_name,
        first_name,
        last_name,
        address: businessAddress,
        slug,
        entityType,
        restaurantType,
        shopProductCategory,
        prefix_name_id,
        subDistrict
      } = selectedBusiness;

      const companyLastName =
        entityType?.id === 1 ? prefixNames?.find(e => e.id === prefix_name_id)?.last_name || '' : '';

      const dist = subDistrict?.district;
      const prov = dist?.province;

      setInitialValues({
        ...defaultInitialValues,
        imagePath: image_path || '',
        settingSlug: slug || '',
        settingTaxNumber: tax_number || '',
        settingEntityTypeId: entityType?.id?.toString() || '',
        settingPrefixNameId: prefix_name_id?.toString() || '',
        settingCompanyName: company_name || '',
        settingFirstName: first_name || '',
        settingLastName: last_name || '',
        settingCompanyLastName: companyLastName || '',
        settingAddress: businessAddress || '',

        settingProvince: prov?.id?.toString() || '',
        settingDistrict: dist?.id?.toString() || '',
        settingSubDistrictId: subDistrict?.id?.toString() || '',
        settingPostalCode: subDistrict?.zip_code || '',

        settingRestaurantTypeId: restaurantType?.id?.toString() || '',
        settingShopProductCategoryTypeId: shopProductCategory?.id?.toString() || ''
      });

      (async () => {
        if (prov?.id) await dispatch(masterHandler.getDistricts(prov.id));
        if (dist?.id) await dispatch(masterHandler.getSubDistricts(dist.id));
      })();
    }
  }, [isCreateMode, selectedBusiness, prefixNames, dispatch]);

  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    try {
      let result;

      const mappedValues = {
        businessGroupId: values.businessGroupId || selectedCustomer?.business_group_id,
        subDistrictId: values.settingSubDistrictId,
        restaurantTypeId: values.settingRestaurantTypeId,
        shopProductCategoryTypeId: values.settingShopProductCategoryTypeId,
        entityTypeId: values.settingEntityTypeId,
        prefixNameId: values.settingPrefixNameId,
        taxNumber: values.settingTaxNumber,
        ...(values.settingEntityTypeId === '2'
          ? {
              firstName: values.settingFirstName,
              lastName: values.settingLastName,
              companyName: undefined
            }
          : {
              firstName: undefined,
              lastName: undefined,
              companyName: values.settingCompanyName
            }),
        address: values.settingAddress,
        slug: values.settingSlug,
        ...(values.imagePath && values.imagePath.startsWith('data:') && {
          imagePath: values.imagePath
        })
      };
      if (isCreateMode) {
        result = await dispatch(customerHandler.createBusiness(mappedValues));
        if (result && onSuccess) {
          resetForm();
          onSuccess(result);
        }
      } else {
        result = await dispatch(customerHandler.updateBusinessGroupInfo(businessID, mappedValues));
        if (result) {
          await dispatch(customerHandler.getBusinessGroupById(businessID));
        }
      }
      setFormDisabled(true);
    } catch (error) {
      console.error('Error processing business info:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleEntityTypeChange = (e, setFieldValue) => {
    setFieldValue('settingEntityTypeId', e.value);
    setFieldValue('settingPrefixNameId', '');
    setFieldValue('settingLastName', '');
    dispatch(masterHandler.getPrefixNames(e.code));
  };

  const handlePrefixChange = (e, setFieldValue) => {
    setFieldValue('settingPrefixNameId', e.value);
    setFieldValue('settingLastName', e.suffix || null);
  };

  const handleProvinceChange = async (e, setFieldValue) => {
    try {
      setFieldValue('settingProvince', e.value);
      setFieldValue('settingDistrict', '');
      setFieldValue('settingSubDistrictId', '');
      setFieldValue('settingPostalCode', '');
      await dispatch(masterHandler.getDistricts(e.value));
    } catch (error) {
      console.error('Error loading districts:', error);
    }
  };

  const handleDistrictChange = async (e, setFieldValue) => {
    try {
      setFieldValue('settingDistrict', e.value);
      setFieldValue('settingSubDistrictId', '');
      setFieldValue('settingPostalCode', '');
      await dispatch(masterHandler.getSubDistricts(e.value));
    } catch (error) {
      console.error('Error loading sub-districts:', error);
    }
  };

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
    >
      {({ handleSubmit, handleChange, touched, errors, values, setFieldValue }) => (
        <Form noValidate onSubmit={handleSubmit}>
          <fieldset disabled={formDisabled}>
            <div className="card-body px-xl-4">
              <div>
                <BusinessImageUpload
                  setFieldValue={setFieldValue}
                  values={values}
                  onUploadSuccess={fileInfo => {
                    setFieldValue('imagePath', fileInfo.base64);
                  }}
                  onUploadError={error => {}}
                />

                <div className="pt-3">
                  <div className="row gx-xl-4">
                    <div className="col-xl-4">
                      <Form.Group className="mb-2 form-st-3 pb-1">
                        <Form.Label className="mb-1">เลขที่ประจำตัวผู้เสียภาษี</Form.Label>
                        <Form.Control
                          type="text"
                          name="settingTaxNumber"
                          className="form-control"
                          placeholder="เลขที่ประจำตัวผู้เสียภาษี"
                          value={values.settingTaxNumber}
                          onChange={handleChange}
                          isInvalid={!!errors.settingTaxNumber}
                          onKeyPress={e => {
                            if (!/[0-9]/.test(e.key)) {
                              e.preventDefault();
                            }
                          }}
                          maxLength={13}
                        />
                        <Form.Control.Feedback type="invalid">{errors.settingTaxNumber}</Form.Control.Feedback>
                      </Form.Group>
                    </div>

                    <div className="col-xl-4">
                      <Form.Group className="mb-2 form-st-3 pb-1">
                        <Form.Label className="mb-1">ชื่อ URL ธุรกิจของคุณ</Form.Label>
                        <Form.Control
                          type="text"
                          name="settingSlug"
                          className="form-control"
                          placeholder="กรอก URL ของคุณ"
                          value={values.settingSlug}
                          onChange={handleChange}
                          isInvalid={!!errors.settingSlug}
                        />
                        <Form.Control.Feedback type="invalid">{errors.settingSlug}</Form.Control.Feedback>
                      </Form.Group>
                    </div>

                    <div className="col-xl-4 align-self-center">
                      <div className="bg-[#D7D9FF] py-1 px-2 text-[#061126] text-[12px] flex items-center rounded font-semibold">
                        <InfoIcon className="text-[#2D3CED] text-[12px] mr-2" />
                        การเปลี่ยน มีผลทำให้ URL การเข้าเว็บไซต์เปลี่ยนไป
                      </div>
                    </div>

                    <div className="col-xl-4">
                      <Form.Group className="mb-2 form-st-3 pb-1">
                        <Form.Label className="mb-1">ประเภทกิจการ</Form.Label>
                        <Select
                          name="settingEntityTypeId"
                          onChange={e => handleEntityTypeChange(e, setFieldValue)}
                          className={!!errors.settingEntityTypeId ? 'w-100 select-st-1 isInvalid' : 'w-100 select-st-1'}
                          options={businessTypeOptions}
                          placeholder="เลือกประเภทกิจการ"
                          value={businessTypeOptions?.filter(item => item.value === values.settingEntityTypeId)}
                        />
                        {errors.settingEntityTypeId && (
                          <div className="invalid-feedback d-block">{errors.settingEntityTypeId}</div>
                        )}
                      </Form.Group>
                    </div>

                    <div className="col-xl-8">
                      <div className="row gx-xl-4">
                        {values.settingEntityTypeId !== '11' && (
                          <div className="col-xl-4">
                            <Form.Group className="mb-2 form-st-3 pb-1">
                              <Form.Label className="mb-1">คำนำหน้า</Form.Label>
                              <Select
                                name="settingPrefixNameId"
                                onChange={e => {
                                  handlePrefixChange(e, setFieldValue);
                                  const companyLastName =
                                    values.settingEntityTypeId === '1'
                                      ? prefixNames?.find(p => p.id === e.id).last_name || ''
                                      : '';
                                  setFieldValue('settingCompanyLastName', companyLastName || null);
                                }}
                                className={
                                  !!errors.settingPrefixNameId ? 'w-100 select-st-1 isInvalid' : 'w-100 select-st-1'
                                }
                                options={prefixTypeOptions}
                                placeholder="เลือกคำนำหน้า"
                                value={prefixTypeOptions?.filter(item => item.value === values.settingPrefixNameId)}
                              />
                              {errors.settingPrefixNameId && (
                                <div className="invalid-feedback d-block">{errors.settingPrefixNameId}</div>
                              )}
                            </Form.Group>
                          </div>
                        )}

                        <div className={`col-xl-${values.settingEntityTypeId === '11' ? '8' : '4'}`}>
                          {values.settingEntityTypeId === '1' ? (
                            <Form.Group className="mb-2 form-st-3 pb-1">
                              <Form.Label className="mb-1">ชื่อกิจการ *</Form.Label>
                              <Form.Control
                                type="text"
                                name="settingCompanyName"
                                className="form-control"
                                placeholder="ชื่อกิจการ"
                                value={values.settingCompanyName}
                                onChange={handleChange}
                                isInvalid={!!errors.settingCompanyName}
                              />
                              <Form.Control.Feedback type="invalid">{errors.settingCompanyName}</Form.Control.Feedback>
                            </Form.Group>
                          ) : (
                            <Form.Group className="mb-2 form-st-3 pb-1">
                              <Form.Label className="mb-1">ชื่อจริง *</Form.Label>
                              <Form.Control
                                type="text"
                                name="settingFirstName"
                                className="form-control"
                                placeholder="ชื่อจริง"
                                value={values.settingFirstName}
                                onChange={handleChange}
                                isInvalid={!!errors.settingFirstName}
                              />
                              <Form.Control.Feedback type="invalid">{errors.settingFirstName}</Form.Control.Feedback>
                            </Form.Group>
                          )}
                        </div>

                        {values.settingEntityTypeId === '1' ? (
                          <div className="col-xl-4">
                            <Form.Group className="mb-2 form-st-3 pb-1">
                              <Form.Label className="mb-1">คำลงท้าย</Form.Label>
                              <Form.Control
                                type="text"
                                name="settingCompanyLastName"
                                className="form-control"
                                placeholder="คำลงท้าย"
                                value={values.settingCompanyLastName}
                                onChange={handleChange}
                                disabled={true}
                                isInvalid={!!errors.settingCompanyLastName}
                              />
                              <Form.Control.Feedback type="invalid">
                                {errors.settingCompanyLastName}
                              </Form.Control.Feedback>
                            </Form.Group>
                          </div>
                        ) : (
                          <div className="col-xl-4">
                            <Form.Group className="mb-2 form-st-3 pb-1">
                              <Form.Label className="mb-1">นามสกุล</Form.Label>
                              <Form.Control
                                type="text"
                                name="settingLastName"
                                className="form-control"
                                placeholder="นามสกุล"
                                value={values.settingLastName}
                                onChange={handleChange}
                                isInvalid={!!errors.settingLastName}
                              />
                              <Form.Control.Feedback type="invalid">{errors.settingLastName}</Form.Control.Feedback>
                            </Form.Group>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="col-xl-12">
                      <Form.Group className="mb-2 form-st-3 pb-1">
                        <Form.Label className="mb-1">ที่อยู่</Form.Label>
                        <Form.Control
                          type="text"
                          name="settingAddress"
                          className="form-control"
                          placeholder="กรอกที่อยู่"
                          value={values.settingAddress}
                          onChange={handleChange}
                          isInvalid={!!errors.settingAddress}
                        />
                        <Form.Control.Feedback type="invalid">{errors.settingAddress}</Form.Control.Feedback>
                      </Form.Group>
                    </div>

                    <div className="col-xl-6">
                      <Form.Group className="mb-2 form-st-3 pb-1">
                        <Form.Label className="mb-1">จังหวัด</Form.Label>
                        <Select
                          name="settingProvince"
                          onChange={e => handleProvinceChange(e, setFieldValue)}
                          className={!!errors.settingProvince ? 'w-100 select-st-1 isInvalid' : 'w-100 select-st-1'}
                          options={
                            provinces?.map(p => ({
                              value: p.id.toString(),
                              label: p.name_th
                            })) || []
                          }
                          placeholder="เลือกจังหวัด"
                          value={
                            provinces
                              ?.filter(p => p.id.toString() === values.settingProvince)
                              .map(p => ({
                                value: p.id.toString(),
                                label: p.name_th
                              })) || null
                          }
                        />
                        {errors.settingProvince && (
                          <div className="invalid-feedback d-block">{errors.settingProvince}</div>
                        )}
                      </Form.Group>
                    </div>

                    <div className="col-xl-6">
                      <Form.Group className="mb-2 form-st-3 pb-1">
                        <Form.Label className="mb-1">เขต/อำเภอ</Form.Label>
                        <Select
                          name="settingDistrict"
                          onChange={e => handleDistrictChange(e, setFieldValue)}
                          className={!!errors.settingDistrict ? 'w-100 select-st-1 isInvalid' : 'w-100 select-st-1'}
                          options={
                            districts?.map(d => ({
                              value: d.id.toString(),
                              label: d.name_th
                            })) || []
                          }
                          placeholder="เลือกเขต/อำเภอ"
                          value={
                            districts
                              ?.filter(d => d.id.toString() === values.settingDistrict)
                              .map(d => ({
                                value: d.id.toString(),
                                label: d.name_th
                              })) || null
                          }
                          isDisabled={!values.settingProvince}
                        />
                        {errors.settingDistrict && (
                          <div className="invalid-feedback d-block">{errors.settingDistrict}</div>
                        )}
                      </Form.Group>
                    </div>

                    <div className="col-xl-6">
                      <Form.Group className="mb-2 form-st-3 pb-1">
                        <Form.Label className="mb-1">แขวง/ตำบล</Form.Label>
                        <Select
                          name="settingSubDistrictId"
                          onChange={e => {
                            setFieldValue('settingSubDistrictId', e.value);
                            setFieldValue('settingPostalCode', e.zip_code || '');
                          }}
                          className={
                            !!errors.settingSubDistrictId ? 'w-100 select-st-1 isInvalid' : 'w-100 select-st-1'
                          }
                          options={
                            subDistricts?.map(s => ({
                              value: s.id.toString(),
                              label: s.name_th,
                              zip_code: s.zip_code,
                              district: s.district,
                              province: s.district?.province || {}
                            })) || []
                          }
                          placeholder="เลือกแขวง/ตำบล"
                          value={
                            subDistricts
                              ?.filter(s => s.id.toString() === values.settingSubDistrictId)
                              .map(s => ({
                                value: s.id.toString(),
                                label: s.name_th
                              })) || null
                          }
                          isDisabled={!values.settingDistrict}
                        />
                        {errors.settingSubDistrictId && (
                          <div className="invalid-feedback d-block">{errors.settingSubDistrictId}</div>
                        )}
                      </Form.Group>
                    </div>

                    <div className="col-xl-6">
                      <Form.Group className="mb-2 form-st-3 pb-1">
                        <Form.Label className="mb-1">รหัสไปรษณีย์</Form.Label>
                        <Form.Control
                          type="text"
                          name="settingPostalCode"
                          className="form-control"
                          placeholder="รหัสไปรษณีย์"
                          value={values.settingPostalCode}
                          disabled
                          isInvalid={!!errors.settingPostalCode}
                        />
                        <Form.Control.Feedback type="invalid">{errors.settingPostalCode}</Form.Control.Feedback>
                      </Form.Group>
                    </div>

                    <div className="col-xl-6">
                      <Form.Group className="mb-2 form-st-3 pb-1">
                        <Form.Label className="mb-1">ประเภทร้านอาหาร</Form.Label>
                        <Select
                          name="settingRestaurantTypeId"
                          onChange={e => {
                            setFieldValue('settingRestaurantTypeId', e.value);
                          }}
                          className={
                            !!errors.settingRestaurantTypeId ? 'w-100 select-st-1 isInvalid' : 'w-100 select-st-1'
                          }
                          options={restaurantTypeOptions}
                          placeholder="เลือกประเภทร้านอาหาร"
                          value={restaurantTypeOptions?.filter(item => item.value === values.settingRestaurantTypeId)}
                        />
                        {errors.settingRestaurantTypeId && (
                          <div className="invalid-feedback d-block">{errors.settingRestaurantTypeId}</div>
                        )}
                      </Form.Group>
                    </div>

                    <div className="col-xl-6">
                      <Form.Group className="mb-2 form-st-3 pb-1">
                        <Form.Label className="mb-1">เลือกประเภทสินค้า</Form.Label>
                        <Select
                          name="settingShopProductCategoryTypeId"
                          onChange={e => {
                            setFieldValue('settingShopProductCategoryTypeId', e.value);
                          }}
                          className={
                            !!errors.settingShopProductCategoryTypeId
                              ? 'w-100 select-st-1 isInvalid'
                              : 'w-100 select-st-1'
                          }
                          options={productTypeOptions}
                          placeholder="เลือกประเภทสินค้า"
                          value={productTypeOptions?.filter(
                            item => item.value === values.settingShopProductCategoryTypeId
                          )}
                        />
                        {errors.settingShopProductCategoryTypeId && (
                          <div className="invalid-feedback d-block">{errors.settingShopProductCategoryTypeId}</div>
                        )}
                      </Form.Group>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </fieldset>

          <div className="text-end pb-4 pe-3">
            {formDisabled ? (
              <Button type="button" className="btn-st-4 mr-3" onClick={() => setFormDisabled(false)}>
                แก้ไข
              </Button>
            ) : (
              <div className="text-end pt-3 btn-group-control">
                <Button
                  type="button"
                  className="btn-st-5 mx-3 bg-transparent !text-[#F95057] !border-[#F95057]"
                  onClick={() => setFormDisabled(true)}
                >
                  ยกเลิก
                </Button>
                <Button type="submit" className="mr-3 !border-[#409FFB]">
                  บันทึก
                </Button>
              </div>
            )}
          </div>
        </Form>
      )}
    </Formik>
  );
};

export default BusinessSettingsTabInfo;
